import {Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  NavigationEnd,
  Params,
  Router,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';
import {Observable, of} from "rxjs";
import {catchError, filter, map} from "rxjs/operators";
import {HttpClient} from '@angular/common/http';

import {uriConsts} from "@shared/constants";
import {AppService} from '@core/services/app.service';
import {AuthService, parseAuthData} from '@core/authentication/auth.service';
import {PageSessionService} from '@core/authentication/page-session.service';
import {PermissionService} from '@core/authentication/permission.service';
import {environment} from '@environments/environment';
import {AdminBusinessContextService} from '../services/admin-business-context.service';


@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  private previousUrl: string = '';
  private isLoadingPermissions = false;

  constructor(
    private router: Router,
    private authService: AuthService,
    private permissionService: PermissionService,
    private http: HttpClient,
    private appService: AppService,
    private adminBusinessContextService: AdminBusinessContextService,
    private pageSessionService: PageSessionService,
  ) {
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe({
        next: event => {
          if (event instanceof NavigationEnd) {
            this.previousUrl = event.url;
          }
        }
      });
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {

    /**
     * Xử lý auth callback từ BE redirect
     * BE redirect về /login?auth={"jwt":"xxx"} sau khi login thành công
     * Logic so sánh JWT:
     * - Nếu JWT mới khác JWT cũ hoặc chưa có authData → gọi /auth/me để lấy data đầy đủ
     * - Nếu JWT giống → giữ nguyên data local, không ghi đè (tránh mất data đã set ở local)
     */
    const authParam = route.queryParams['auth'];
    if (authParam) {
      return this.handleAuthCallback(authParam, route.queryParams);
    }

    // Reset hiển thị page trên menu profile mỗi khi chuyển trang.
    this.authService.pageDisplay = null;
    //this.chatbotService.botName = null;

    // Nếu chưa đăng nhập, redirect đăng nhập.
    if (!this.authService.getUid()) {
      // Xóa dữ liệu auth cũ (nếu có) rồi redirect về login qua Angular router,
      // không gọi logout() vì logout() dùng document.location.href gây reload trang không cần thiết.
      this.authService.clearAuthUser();
      return this.router.parseUrl(uriConsts.auth.login);
    }

    // -----------------------------------------------------------------------------------------
    // XỬ LÝ CHUYỂN NGỮ CẢNH DOANH NGHIỆP CHO ADMIN/SUPERADMIN KHI CÓ PARAM ?businessId=xxx
    // TUYỆT ĐỐI CHỈ ÁP DỤNG KHI USER LÀ ADMIN/SUPERADMIN (authService.isAdmin() === true).
    // NGƯỜI DÙNG THƯỜNG HOÀN TOÀN KHÔNG BỊ ẢNH HƯỞNG.
    // -----------------------------------------------------------------------------------------
    const urlBusinessId = route.queryParams['businessId'];
    if (urlBusinessId && this.authService.isAdmin()) {
      const bId = Number(urlBusinessId);
      if (Number.isFinite(bId) && bId > 0) {
        // Trường hợp 1: Nếu Admin ĐÃ LOGIN doanh nghiệp này (đã có quyền và đã chọn page lúc login)
        // -> Lấy các page đang chọn như bình thường của doanh nghiệp này, KHÔNG kích hoạt load toàn bộ page!
        if (this.authService.isBusinessLoggedIn(bId)) {
          this.authService.restoreLoggedInBusiness(bId);
          return this.checkRouteAccess(route, state);
        }

        // Trường hợp 2: Doanh nghiệp này Admin KHÔNG LOGIN (doanh nghiệp lạ của khách hàng cần check)
        // -> Kích hoạt tính năng của Admin để gọi API load toàn bộ page của doanh nghiệp này cho Admin check!
        if (!this.adminBusinessContextService.isAlreadyInBusiness(bId)) {
          return this.adminBusinessContextService.switchBusinessForAdmin(bId).pipe(
            map(() => this.checkRouteAccess(route, state)),
            catchError((err) => {
              console.error('[AuthGuard] Lỗi khi chuyển đổi doanh nghiệp cho admin:', err);
              return of(this.checkRouteAccess(route, state));
            })
          );
        }
      }
    }

    // `usr` chỉ là cache. Khôi phục session Page ở BE trước khi vào chat để
    // mọi request phía sau không chạy với active page = null.
    return this.ensurePageSessionBeforeRoute(route, state);
  }

  private ensurePageSessionBeforeRoute(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | boolean | UrlTree {
    const currentPath = state.url.split('?')[0];
    if (currentPath === uriConsts.login || !this.authService.hasJoinPages()) {
      return this.checkRouteAccess(route, state);
    }

    return this.pageSessionService.ensureActivePageSession().pipe(
      map(isRestored => isRestored
        ? this.checkRouteAccess(route, state)
        : this.router.parseUrl(uriConsts.login)),
      catchError(() => of(this.router.parseUrl(uriConsts.login)))
    );
  }

  /**
   * Hàm kiểm tra quyền truy cập route chuẩn của hệ thống (giữ nguyên logic gốc).
   * Được tách ra để hỗ trợ luồng async khi Admin cần nạp dữ liệu doanh nghiệp trước.
   */
  private checkRouteAccess(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree {
    // Nếu chưa chọn pages để quản lý => redirect ra trang chọn page.
    if (!this.authService.hasJoinPages()) {
      // Nếu là module Ads, không bắt buộc qua trang chọn page
      if (environment.isAdsModule) {
        return true;
      }

      if (state.url.split('?')[0] == uriConsts.login) {
        // nếu đang vào trang chọn page -> ko redirect nữa
        return true;
      }
      return this.router.parseUrl(uriConsts.login);
    }

    // Xử lý quyền custom role hoặc role hệ thống
    const isCustomRole = this.authService.isCustomRole();
    const currentPermissions = this.permissionService.getCurrentPermissions();
    const activeBusiness = this.authService.getActiveBusiness();
    const dataRoute = route.data;
    const requiredPermission = dataRoute?.['permission'];

    // Nếu là custom role nhưng chưa có permissions, load permissions trước
    if (isCustomRole && currentPermissions.length === 0 && !this.isLoadingPermissions) {
      if (activeBusiness && activeBusiness?.roleId) {
        this.isLoadingPermissions = true;
        this.permissionService.loadRolePermissions(activeBusiness.roleId, activeBusiness.id).subscribe({
          next: () => {
            this.isLoadingPermissions = false;
            // Điều hướng lại url sau khi permissions được tải
            this.router.navigateByUrl(state.url);
          },
          error: (error) => {
            console.error('AuthGuard: Failed to load permissions:', error);
            this.isLoadingPermissions = false;
          }
        });
      }
      // Tạm thời cho phép truy cập để tránh redirect loop
      return true;
    }

    // Nếu là custom role và route yêu cầu permission cụ thể
    if (isCustomRole && requiredPermission) {
      if (currentPermissions && currentPermissions.some(p => String(p).trim() == String(requiredPermission).trim())) {
        return true;
      }
      // Không có quyền → redirect
      if (dataRoute?.['hasRoleRedirect'] && dataRoute['hasRoleRedirect'] !== state.url) {
        return this.router.parseUrl(dataRoute['hasRoleRedirect']);
      }
      return this.router.parseUrl(this.appService.getDefaultRedirectUrl());
    }

    // Nếu là custom role nhưng route không yêu cầu permission cụ thể → cho phép truy cập
    if (isCustomRole && !requiredPermission) {
      return true;
    }

    // Kiểm tra quyền hệ thống (system role)
    if (this.authService.isAllowed(dataRoute['role'])) {
      return true;
    } else if (dataRoute && dataRoute?.['hasRoleRedirect'] && dataRoute['hasRoleRedirect']) {
      return this.router.parseUrl(dataRoute['hasRoleRedirect']);
    }
    return this.router.parseUrl(this.appService.getDefaultRedirectUrl());
  }

  /**
   * Xử lý JWT nhận được từ URL redirect của BE
   * So sánh với JWT đang có trong localStorage:
   * - Khác hoặc chưa có → gọi API /auth/me lấy đầy đủ authData
   * - Giống → giữ nguyên data cũ (tránh mất data đã set thêm ở local)
   */
  private handleAuthCallback(authJson: string, queryParams: Params = {}): Observable<boolean | UrlTree> {
    try {
      const authMinimal = JSON.parse(authJson);
      if (!authMinimal?.jwt) {
        console.error('AuthGuard: JWT không hợp lệ từ redirect');
        return of(this.router.parseUrl(uriConsts.auth.login));
      }

      // Lấy JWT hiện tại từ localStorage
      const currentToken = this.authService.getAuthorizationToken();
      const newJwt = authMinimal.jwt;

      // Nếu JWT không thay đổi và đã có thông tin user đầy đủ (UID)
      // thì giữ nguyên data cũ, xóa param trên URL và cho qua
      if (currentToken && currentToken === `Bearer ${newJwt}` && this.authService.getUid()) {
        return of(this.getAuthCallbackRedirectUrlTree(queryParams));
      }

      // JWT mới hoặc chưa có đầy đủ thông tin → lưu JWT tạm thời để gọi API
      this.authService.setAuthorization(authJson);

      // Gọi API /auth/me để lấy đầy đủ thông tin user
      return this.http.get<any>(`${environment.baseUrlApi}${uriConsts.auth.me}`).pipe(
        map((res: any) => {
          // /auth/me trả data là chuỗi JSON: không parse thì auth bị lưu thành chuỗi, FE mất đăng nhập
          // trong khi phiên BE vẫn còn → lần đăng nhập sau bị BE chặn CSRF (403)
          const authData = parseAuthData(res?.data);
          if (authData?.jwt) {
            // Lưu đầy đủ authData vào localStorage
            this.authService.setAuthorization(JSON.stringify(authData));
            // Redirect về trang chủ của module để xóa bỏ param auth trên URL
            return this.getAuthCallbackRedirectUrlTree(queryParams);
          }
          // API trả về lỗi → redirect về trang login
          return this.router.createUrlTree([uriConsts.auth.login]);
        }),
        catchError((err) => {
          console.error('AuthGuard: Lỗi khi gọi /auth/me', err);
          return of(this.router.createUrlTree([uriConsts.auth.login]));
        })
      );
    } catch (e) {
      console.error('AuthGuard: Lỗi parse auth param', e);
      return of(this.router.createUrlTree([uriConsts.auth.login]));
    }
  }

  private getAuthCallbackRedirectUrlTree(queryParams: Params): UrlTree {
    if (queryParams['extendPage']) {
      const extendQueryParams: Params = {
        extendPage: queryParams['extendPage'],
      };
      Object.keys(queryParams).forEach(key => {
        if (key === 'pageIds' || key === 'pageIds[]' || /^pageIds\[\d+\]$/.test(key)) {
          extendQueryParams[key] = queryParams[key];
        }
      });

      return this.router.createUrlTree([uriConsts.login], {
        queryParams: extendQueryParams
      });
    }

    return this.router.createUrlTree([this.appService.getDefaultRedirectUrl()]);
  }

}
